import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { 
  MapPin, Phone, Globe, Mail, Clock, Star, ChevronRight, ArrowLeft,
  Share2, Heart, MessageCircle, ExternalLink, CheckCircle, Image,
  Building2, Sparkles, Shield, Award, Users, Calendar, Navigation,
  Wifi, Car, Coffee, Utensils, Waves, Trees, Sun, Moon
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Category-specific icons and colors
const CATEGORY_CONFIG = {
  'accommodation': { icon: Building2, color: 'sky', gradient: 'from-sky-500 to-blue-600', emoji: '🏨' },
  'restaurants': { icon: Utensils, color: 'orange', gradient: 'from-orange-500 to-red-500', emoji: '🍽️' },
  'attractions': { icon: Sparkles, color: 'purple', gradient: 'from-purple-500 to-pink-500', emoji: '🎡' },
  'tours': { icon: Navigation, color: 'emerald', gradient: 'from-emerald-500 to-teal-500', emoji: '🧭' },
  'activities': { icon: Waves, color: 'cyan', gradient: 'from-cyan-500 to-blue-500', emoji: '🏄' },
  'shopping': { icon: Heart, color: 'pink', gradient: 'from-pink-500 to-rose-500', emoji: '🛍️' },
  'nightlife': { icon: Moon, color: 'violet', gradient: 'from-violet-500 to-purple-600', emoji: '🌙' },
  'nature': { icon: Trees, color: 'green', gradient: 'from-green-500 to-emerald-500', emoji: '🌿' },
  'default': { icon: Star, color: 'amber', gradient: 'from-amber-500 to-orange-500', emoji: '⭐' }
};

const BusinessPage = () => {
  const { businessId, cityId, categorySlug, slug } = useParams();
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inquiryForm, setInquiryForm] = useState({
    customer_name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [selectedImage, setSelectedImage] = useState(0);
  const [isFavorite, setIsFavorite] = useState(false);

  useEffect(() => {
    fetchBusiness();
  }, [businessId, cityId, categorySlug, slug]);

  const fetchBusiness = async () => {
    try {
      setLoading(true);
      let url;
      if (cityId && categorySlug && slug) {
        url = `${API_URL}/api/hotspots/${cityId}/${categorySlug}/business/${slug}`;
      } else {
        url = `${API_URL}/api/hotspots/businesses/${businessId}`;
      }
      
      const response = await fetch(url);
      if (!response.ok) throw new Error('Business not found');
      const data = await response.json();
      setBusiness(data);
    } catch (error) {
      console.error('Error fetching business:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInquirySubmit = async (e) => {
    e.preventDefault();
    if (!inquiryForm.customer_name || !inquiryForm.email || !inquiryForm.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setSubmitting(true);
      const response = await fetch(`${API_URL}/api/hotspots/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          business_id: business?.id || businessId,
          ...inquiryForm
        })
      });

      if (!response.ok) throw new Error('Failed to submit inquiry');

      toast.success('Your inquiry has been sent! The business will contact you soon.');
      setInquiryForm({ customer_name: '', email: '', phone: '', message: '' });
    } catch (error) {
      toast.error('Failed to send inquiry. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: business?.name,
          text: `Check out ${business?.name} on Australian Hotspots!`,
          url: window.location.href
        });
      } catch (err) {
        console.log('Share cancelled');
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success('Link copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-sky-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-sky-400 text-lg">Discovering this gem...</p>
        </div>
      </div>
    );
  }

  if (!business) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-6">
            <Building2 className="w-10 h-10 text-slate-600" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-4">Business Not Found</h1>
          <p className="text-slate-400 mb-6">This listing may have been removed or the URL is incorrect.</p>
          <Link to="/hotspots" className="inline-flex items-center gap-2 px-6 py-3 bg-sky-500 hover:bg-sky-600 text-white rounded-full font-medium transition">
            <ArrowLeft className="w-4 h-4" /> Back to Australian Hotspots
          </Link>
        </div>
      </div>
    );
  }

  const locationName = business.location_id?.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  const categoryName = business.category_id?.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  const config = CATEGORY_CONFIG[business.category_id] || CATEGORY_CONFIG['default'];
  const CategoryIcon = config.icon;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Floating Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-slate-900/80 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center justify-between">
            <nav className="flex items-center gap-2 text-sm text-slate-400 overflow-x-auto">
              <Link to="/hotspots" className="hover:text-white whitespace-nowrap flex items-center gap-1">
                <span className="text-sky-400 font-bold">Australian</span>
                <span className="text-orange-400 font-bold">Hotspots</span>
              </Link>
              <ChevronRight className="h-4 w-4 flex-shrink-0 text-slate-600" />
              <Link to={`/hotspots/${business.location_id}`} className="hover:text-white whitespace-nowrap">
                {locationName}
              </Link>
              <ChevronRight className="h-4 w-4 flex-shrink-0 text-slate-600" />
              <span className="text-white font-medium whitespace-nowrap truncate max-w-[150px]">{business.name}</span>
            </nav>
            
            <div className="flex items-center gap-2">
              <button 
                onClick={() => setIsFavorite(!isFavorite)}
                className={`p-2 rounded-full transition ${isFavorite ? 'bg-pink-500/20 text-pink-400' : 'bg-white/10 text-slate-400 hover:text-white'}`}
              >
                <Heart className={`w-5 h-5 ${isFavorite ? 'fill-current' : ''}`} />
              </button>
              <button 
                onClick={handleShare}
                className="p-2 rounded-full bg-white/10 text-slate-400 hover:text-white transition"
              >
                <Share2 className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-20">
        {/* Background with gradient overlay */}
        <div className="absolute inset-0 overflow-hidden">
          {business.images && business.images.length > 0 ? (
            <img 
              src={business.images[0]} 
              alt={business.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className={`w-full h-full bg-gradient-to-br ${config.gradient} opacity-20`}></div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/80 to-slate-900/40"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left - Content */}
            <div>
              {/* Badges */}
              <div className="flex flex-wrap items-center gap-3 mb-6">
                <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r ${config.gradient} text-white font-medium shadow-lg`}>
                  <CategoryIcon className="w-4 h-4" />
                  {categoryName}
                </span>
                {business.verified && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-400 text-sm font-medium border border-emerald-500/30">
                    <CheckCircle className="w-4 h-4" /> Verified
                  </span>
                )}
                {business.featured && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-amber-500/20 text-amber-400 text-sm font-medium border border-amber-500/30">
                    <Award className="w-4 h-4" /> Featured
                  </span>
                )}
              </div>

              {/* Title */}
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-4 leading-tight">
                {business.name}
              </h1>

              {/* Location & Rating */}
              <div className="flex flex-wrap items-center gap-4 mb-6">
                <span className="flex items-center gap-2 text-slate-300">
                  <MapPin className="w-5 h-5 text-sky-400" />
                  {locationName}, Australia
                </span>
                {business.rating > 0 && (
                  <span className="flex items-center gap-2 px-3 py-1 bg-amber-500/20 rounded-full">
                    <Star className="w-5 h-5 text-amber-400 fill-amber-400" />
                    <span className="text-white font-bold">{business.rating.toFixed(1)}</span>
                    <span className="text-slate-400">({business.reviews_count || 0} reviews)</span>
                  </span>
                )}
                {business.price_range && (
                  <span className="text-emerald-400 font-bold text-xl">{business.price_range}</span>
                )}
              </div>

              {/* Description */}
              {business.description && (
                <p className="text-lg text-slate-300 mb-8 leading-relaxed">
                  {business.description}
                </p>
              )}

              {/* CTA Buttons */}
              <div className="flex flex-wrap gap-4">
                {business.website && (
                  <a 
                    href={business.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r ${config.gradient} hover:opacity-90 text-white rounded-full font-bold text-lg shadow-2xl shadow-${config.color}-500/30 transform hover:scale-105 transition-all`}
                  >
                    <Globe className="w-6 h-6" />
                    Visit Official Website
                    <ExternalLink className="w-5 h-5" />
                  </a>
                )}
                {business.phone && (
                  <a 
                    href={`tel:${business.phone}`}
                    className="inline-flex items-center gap-3 px-6 py-4 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white rounded-full font-semibold border border-white/20 transition-all"
                  >
                    <Phone className="w-5 h-5" />
                    Call Now
                  </a>
                )}
              </div>
            </div>

            {/* Right - Image/Card */}
            <div className="relative">
              <div className={`absolute -inset-4 bg-gradient-to-r ${config.gradient} rounded-3xl blur-2xl opacity-30`}></div>
              <div className="relative bg-slate-800/50 backdrop-blur-xl rounded-3xl overflow-hidden border border-white/10 shadow-2xl">
                {/* Image area */}
                <div className="aspect-[4/3] bg-gradient-to-br from-slate-700 to-slate-800 relative">
                  {business.images && business.images.length > 0 ? (
                    <img 
                      src={business.images[selectedImage]} 
                      alt={business.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <div className={`w-24 h-24 rounded-full bg-gradient-to-br ${config.gradient} flex items-center justify-center mb-4`}>
                        <CategoryIcon className="w-12 h-12 text-white" />
                      </div>
                      <span className="text-5xl mb-2">{config.emoji}</span>
                      <p className="text-slate-500">Photos coming soon</p>
                    </div>
                  )}
                </div>

                {/* Thumbnail strip */}
                {business.images && business.images.length > 1 && (
                  <div className="flex gap-2 p-3 overflow-x-auto bg-slate-900/50">
                    {business.images.map((img, index) => (
                      <button
                        key={index}
                        onClick={() => setSelectedImage(index)}
                        className={`w-16 h-12 rounded-lg overflow-hidden flex-shrink-0 border-2 transition-all ${
                          selectedImage === index ? 'border-sky-500 scale-110' : 'border-transparent hover:border-white/30'
                        }`}
                      >
                        <img src={img} alt="" className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                )}

                {/* Quick Info Bar */}
                <div className="p-4 border-t border-white/10">
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-4">
                      {business.address && (
                        <span className="flex items-center gap-1 text-slate-400">
                          <MapPin className="w-4 h-4" />
                          <span className="truncate max-w-[150px]">{business.address.split(',')[0]}</span>
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {business.verified && <Shield className="w-5 h-5 text-emerald-400" />}
                      {business.featured && <Award className="w-5 h-5 text-amber-400" />}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Smart Choice Banner */}
      <section className="relative py-12 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/10 via-sky-500/10 to-purple-500/10"></div>
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-emerald-500/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-slate-800/60 backdrop-blur-xl rounded-3xl p-8 md:p-12 border border-white/10 shadow-2xl">
            <div className="text-center max-w-3xl mx-auto">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/20 rounded-full text-emerald-400 text-sm font-medium mb-6 border border-emerald-500/30">
                <CheckCircle className="w-4 h-4" />
                You've Made a Smart Choice
              </div>
              
              <h2 className="text-3xl md:text-4xl font-black text-white mb-4">
                Congratulations on Discovering <span className={`bg-gradient-to-r ${config.gradient} bg-clip-text text-transparent`}>{business.name}</span>
              </h2>
              
              <p className="text-lg text-slate-300 mb-8 leading-relaxed">
                You're one step closer to an unforgettable {categoryName?.toLowerCase()} experience in beautiful {locationName}. 
                By choosing to explore through <span className="text-sky-400 font-semibold">Australian Hotspots</span>, 
                you're accessing our curated network of verified partners who offer exceptional service and authentic local experiences.
              </p>
              
              <div className="grid sm:grid-cols-3 gap-6 mb-8">
                <div className="bg-white/5 rounded-2xl p-5 border border-white/10">
                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center mx-auto mb-4">
                    <Shield className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="font-bold text-white mb-2">Verified & Trusted</h3>
                  <p className="text-sm text-slate-400">Every listing is verified for authenticity and quality standards</p>
                </div>
                <div className="bg-white/5 rounded-2xl p-5 border border-white/10">
                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center mx-auto mb-4">
                    <Award className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="font-bold text-white mb-2">Best Value Guarantee</h3>
                  <p className="text-sm text-slate-400">Our partners offer competitive rates you won't find elsewhere</p>
                </div>
                <div className="bg-white/5 rounded-2xl p-5 border border-white/10">
                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-sky-400 to-blue-500 flex items-center justify-center mx-auto mb-4">
                    <Users className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="font-bold text-white mb-2">Local Expertise</h3>
                  <p className="text-sm text-slate-400">Get insider tips and authentic experiences from locals who know best</p>
                </div>
              </div>
              
              <p className="text-slate-400 text-sm">
                When you book through our partner's website, you're supporting local Australian businesses while enjoying peace of mind with our quality guarantee.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What to Expect Section */}
      <section className="relative py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-slate-800/50 backdrop-blur-xl rounded-3xl p-8 border border-white/10">
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
              <Sparkles className="w-6 h-6 text-amber-400" />
              What You Can Expect at {business.name}
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-sky-500/20 flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-5 h-5 text-sky-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">Warm Australian Hospitality</h3>
                    <p className="text-sm text-slate-400">Experience the famous friendly service that makes Australia a world-class destination</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-5 h-5 text-emerald-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">Prime {locationName} Location</h3>
                    <p className="text-sm text-slate-400">Situated perfectly to explore everything {locationName} has to offer</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-5 h-5 text-purple-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">Quality You Can Trust</h3>
                    <p className="text-sm text-slate-400">Rated {business.rating?.toFixed(1) || '4.5'}+ stars by {business.reviews_count || 'hundreds of'} satisfied guests</p>
                  </div>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-5 h-5 text-amber-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">Exceptional Value</h3>
                    <p className="text-sm text-slate-400">Competitive pricing with no hidden fees when you book direct</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-pink-500/20 flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-5 h-5 text-pink-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">Memorable Experiences</h3>
                    <p className="text-sm text-slate-400">Create lasting memories in one of Australia's most stunning regions</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-5 h-5 text-cyan-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">Easy Direct Booking</h3>
                    <p className="text-sm text-slate-400">Book directly with the property for the best rates and flexible options</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* CTA within section */}
            <div className="mt-8 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4">
              <p className="text-slate-300">
                Ready to book your stay at <span className="text-white font-semibold">{business.name}</span>?
              </p>
              {business.website && (
                <a 
                  href={business.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r ${config.gradient} hover:opacity-90 text-white rounded-full font-bold shadow-lg transform hover:scale-105 transition-all`}
                >
                  <Globe className="w-5 h-5" />
                  Book Direct & Save
                  <ExternalLink className="w-4 h-4" />
                </a>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Details Section */}
      <section className="relative py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Left - Main Info */}
            <div className="lg:col-span-2 space-y-8">
              {/* Features */}
              {business.features && business.features.length > 0 && (
                <div className="bg-slate-800/50 backdrop-blur-xl rounded-2xl p-6 border border-white/10">
                  <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-purple-400" />
                    Features & Amenities
                  </h2>
                  <div className="flex flex-wrap gap-2">
                    {business.features.map((feature, index) => (
                      <span 
                        key={index}
                        className="px-4 py-2 bg-white/5 text-slate-300 rounded-full text-sm border border-white/10 hover:bg-white/10 transition"
                      >
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Contact Info Card */}
              <div className="bg-slate-800/50 backdrop-blur-xl rounded-2xl p-6 border border-white/10">
                <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                  <MessageCircle className="w-5 h-5 text-sky-400" />
                  Contact Information
                </h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  {business.address && (
                    <div className="flex items-start gap-4 p-4 bg-white/5 rounded-xl border border-white/10">
                      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${config.gradient} flex items-center justify-center flex-shrink-0`}>
                        <MapPin className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <div className="text-sm text-slate-500 mb-1">Address</div>
                        <div className="text-white">{business.address}</div>
                      </div>
                    </div>
                  )}
                  {business.phone && (
                    <a href={`tel:${business.phone}`} className="flex items-start gap-4 p-4 bg-white/5 rounded-xl border border-white/10 hover:bg-white/10 transition group">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center flex-shrink-0">
                        <Phone className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <div className="text-sm text-slate-500 mb-1">Phone</div>
                        <div className="text-white group-hover:text-sky-400 transition">{business.phone}</div>
                      </div>
                    </a>
                  )}
                  {business.email && (
                    <a href={`mailto:${business.email}`} className="flex items-start gap-4 p-4 bg-white/5 rounded-xl border border-white/10 hover:bg-white/10 transition group">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center flex-shrink-0">
                        <Mail className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <div className="text-sm text-slate-500 mb-1">Email</div>
                        <div className="text-white group-hover:text-sky-400 transition">{business.email}</div>
                      </div>
                    </a>
                  )}
                  {business.website && (
                    <a href={business.website} target="_blank" rel="noopener noreferrer" className="flex items-start gap-4 p-4 bg-white/5 rounded-xl border border-white/10 hover:bg-white/10 transition group">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center flex-shrink-0">
                        <Globe className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <div className="text-sm text-slate-500 mb-1">Website</div>
                        <div className="text-white group-hover:text-sky-400 transition flex items-center gap-1">
                          Visit website <ExternalLink className="h-4 w-4" />
                        </div>
                      </div>
                    </a>
                  )}
                </div>
              </div>

              {/* Why Book Section */}
              <div className="bg-gradient-to-br from-sky-500/10 to-purple-500/10 rounded-2xl p-6 border border-white/10">
                <h2 className="text-xl font-bold text-white mb-4">Why Visit {business.name}?</h2>
                <div className="grid sm:grid-cols-3 gap-4">
                  <div className="text-center p-4">
                    <div className="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-3">
                      <CheckCircle className="w-6 h-6 text-emerald-400" />
                    </div>
                    <h3 className="font-semibold text-white mb-1">Verified Listing</h3>
                    <p className="text-sm text-slate-400">Authentic, verified business details</p>
                  </div>
                  <div className="text-center p-4">
                    <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-3">
                      <Star className="w-6 h-6 text-amber-400" />
                    </div>
                    <h3 className="font-semibold text-white mb-1">Highly Rated</h3>
                    <p className="text-sm text-slate-400">Loved by {business.reviews_count || 'many'} visitors</p>
                  </div>
                  <div className="text-center p-4">
                    <div className="w-12 h-12 rounded-full bg-sky-500/20 flex items-center justify-center mx-auto mb-3">
                      <MapPin className="w-6 h-6 text-sky-400" />
                    </div>
                    <h3 className="font-semibold text-white mb-1">Prime Location</h3>
                    <p className="text-sm text-slate-400">In the heart of {locationName}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right - Sidebar */}
            <div className="space-y-6">
              {/* Main CTA Card */}
              <div className={`bg-gradient-to-br ${config.gradient} rounded-2xl p-6 text-white shadow-2xl`}>
                <h2 className="text-2xl font-bold mb-2">Ready to Visit?</h2>
                <p className="text-white/80 mb-6">Book directly with {business.name} for the best rates and availability.</p>
                
                {business.website && (
                  <a 
                    href={business.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 w-full py-4 bg-white text-slate-900 rounded-xl font-bold text-lg hover:bg-slate-100 transition shadow-lg"
                  >
                    <Globe className="w-5 h-5" />
                    Book / Visit Website
                    <ExternalLink className="w-5 h-5" />
                  </a>
                )}
                
                {business.phone && (
                  <a 
                    href={`tel:${business.phone}`}
                    className="flex items-center justify-center gap-2 w-full py-3 mt-3 bg-white/20 backdrop-blur-sm rounded-xl font-semibold hover:bg-white/30 transition"
                  >
                    <Phone className="w-5 h-5" />
                    {business.phone}
                  </a>
                )}
              </div>

              {/* Inquiry Form */}
              <div className="bg-slate-800/50 backdrop-blur-xl rounded-2xl p-6 border border-white/10">
                <h2 className="font-bold text-white mb-4 flex items-center gap-2">
                  <MessageCircle className="h-5 w-5 text-sky-400" />
                  Send an Inquiry
                </h2>
                <form onSubmit={handleInquirySubmit} className="space-y-4">
                  <div>
                    <Input
                      type="text"
                      value={inquiryForm.customer_name}
                      onChange={(e) => setInquiryForm({...inquiryForm, customer_name: e.target.value})}
                      placeholder="Your Name *"
                      required
                      className="bg-white/5 border-white/10 text-white placeholder-slate-500"
                      data-testid="inquiry-name"
                    />
                  </div>
                  <div>
                    <Input
                      type="email"
                      value={inquiryForm.email}
                      onChange={(e) => setInquiryForm({...inquiryForm, email: e.target.value})}
                      placeholder="Email *"
                      required
                      className="bg-white/5 border-white/10 text-white placeholder-slate-500"
                      data-testid="inquiry-email"
                    />
                  </div>
                  <div>
                    <Input
                      type="tel"
                      value={inquiryForm.phone}
                      onChange={(e) => setInquiryForm({...inquiryForm, phone: e.target.value})}
                      placeholder="Phone (optional)"
                      className="bg-white/5 border-white/10 text-white placeholder-slate-500"
                      data-testid="inquiry-phone"
                    />
                  </div>
                  <div>
                    <Textarea
                      value={inquiryForm.message}
                      onChange={(e) => setInquiryForm({...inquiryForm, message: e.target.value})}
                      placeholder="Your message... *"
                      rows={3}
                      required
                      className="bg-white/5 border-white/10 text-white placeholder-slate-500"
                      data-testid="inquiry-message"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={submitting}
                    className={`w-full bg-gradient-to-r ${config.gradient} hover:opacity-90`}
                    data-testid="inquiry-submit"
                  >
                    {submitting ? 'Sending...' : 'Send Inquiry'}
                  </Button>
                </form>
              </div>

              {/* Explore More */}
              <div className="bg-slate-800/50 backdrop-blur-xl rounded-2xl p-6 border border-white/10">
                <h3 className="font-semibold text-white mb-3">Explore More in {locationName}</h3>
                <Link
                  to={`/hotspots/${business.location_id}/${business.category_id}`}
                  className="flex items-center justify-between p-3 bg-white/5 rounded-xl hover:bg-white/10 transition text-slate-300 hover:text-white"
                >
                  <span>More {categoryName}</span>
                  <ChevronRight className="w-5 h-5" />
                </Link>
                <Link
                  to={`/hotspots/${business.location_id}`}
                  className="flex items-center justify-between p-3 bg-white/5 rounded-xl hover:bg-white/10 transition text-slate-300 hover:text-white mt-2"
                >
                  <span>All {locationName} Hotspots</span>
                  <ChevronRight className="w-5 h-5" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black/50 backdrop-blur-xl border-t border-white/10 py-12 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center text-center mb-10">
            <div className="relative mb-6">
              <div className="absolute inset-0 bg-gradient-to-r from-orange-500/20 to-red-500/20 blur-3xl rounded-full"></div>
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/28a7e231-ff1b-4b8f-915f-47798802b078/images/2fa9ddf7953c616448f48ed011e225e9e3469523a5a5c8f53c562308f1d829cc.png" 
                alt="Lionheart Digital" 
                className="h-20 md:h-28 w-auto relative z-10 drop-shadow-2xl"
              />
            </div>
            <h3 className="text-xl md:text-2xl font-black tracking-tight mb-2">
              <span className="text-white">LIONHEART</span>
              <span className="text-orange-500"> DIGITAL</span>
            </h3>
            <p className="text-sm md:text-base font-bold italic text-transparent bg-clip-text bg-gradient-to-r from-orange-400 via-red-500 to-orange-400">
              "They lit the fire. We became it."
            </p>
          </div>
          <div className="border-t border-white/10 pt-6 text-center">
            <Link to="/hotspots" className="text-xl font-bold">
              <span className="text-sky-400">Australian</span>
              <span className="text-orange-400"> Hotspots</span>
            </Link>
            <p className="text-slate-500 text-sm mt-4">
              &copy; {new Date().getFullYear()} Australian Hotspots. A Lionheart Digital Asset.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default BusinessPage;
